import React, { Fragment, useRef } from "react";
import { Menu, Transition } from "@headlessui/react";

import decideDropdownXPosition from "../helpers/decideDropdownXPosition";
import decideDropdownCornerRadius from "../helpers/decideDropdownCornerRadius";

import Typography from "../../../common/Typography/Typography";
import Icon, { Icons } from "../../../common/Icon/Icon";
import Dropdown from "../Dropdown/Dropdown";
import DropdownItem from "../Dropdown/DropdownItem/DropdownItem";

import styles from "./ViewDropdown.module.css";

interface Option {
    label: string;
    value: string;
    icon: Icons;
}

interface Props<T> {
    label?: string;
    title?: string;
    options: T[];
    value?: Option["value"];
    onChange: (value: string) => void;
}

const ViewDropdown = <T extends Option>({ label = "View", options, value, onChange, title }: Props<T>) => {
    const position = useRef({});

    const handlePosition = (e: React.MouseEvent) => {
        const newPosition = decideDropdownXPosition(e);
        position.current = {
            ...newPosition,
            ...decideDropdownCornerRadius(Object.keys(newPosition).includes("left")),
        };
    };

    return (
        <Menu as="div" className="relative z-100 sm:z-10">
            {({ open, close }) => (
                <Fragment>
                    <Menu.Button
                        className={`${styles.viewDropdownTrigger} ${open ? styles.viewDropdownTriggerActive : ""}`}
                        title={title}
                        onClick={handlePosition}
                    >
                        <Typography color="midnight" variant="meta">
                            {label}
                        </Typography>
                        <Icon icon="list" />
                    </Menu.Button>
                    <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                    >
                        <div className={styles.viewDropdown} style={{ ...position.current }}>
                            <Dropdown>
                                {options.map((option) => (
                                    <DropdownItem
                                        key={option.value}
                                        title={option.label}
                                        icon={option.icon}
                                        isActive={option.value === value}
                                        onClick={() => {
                                            close();
                                            onChange(option.value);
                                        }}
                                    >
                                        {option.label}
                                    </DropdownItem>
                                ))}
                            </Dropdown>
                        </div>
                    </Transition>
                </Fragment>
            )}
        </Menu>
    );
};

export default ViewDropdown;
